import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { WaterSavedSensorDetail } from '@metron/shared';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { Input } from './ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table.tsx';

interface WaterSavedSensorTableProps {
  sensors: WaterSavedSensorDetail[];
  total: number;
  page: number;
  limit: number;
  /** Pre-formatted period label, e.g. "July 2026" or "Q1 2026" */
  label: string;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onSensorClick?: (sensorId: number) => void;
  isLoading?: boolean;
  error?: string | null;
}

export function WaterSavedSensorTable({
  sensors,
  total,
  page,
  limit,
  label,
  onPageChange,
  onLimitChange,
  onSensorClick,
  isLoading,
  error,
}: WaterSavedSensorTableProps) {
  const totalPages = Math.ceil(total / limit);
  const canPreviousPage = page > 1;
  const canNextPage = page < totalPages;

  // State for page jump input
  const [pageInput, setPageInput] = useState('');

  const handlePageJump = () => {
    const pageNum = parseInt(pageInput, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
    }
    setPageInput('');
  };

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meters with Fixed Leaks</CardTitle>
        <CardDescription>
          {label} • {total} meter{total !== 1 ? 's' : ''} with fixed leaks
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {onSensorClick && sensors.length > 0 && (
          <p className='text-sm text-muted-foreground'>
            <span className='font-medium text-foreground'>Click on a row</span>{' '}
            to view the validation chart showing daily water usage, leak detection, and water saved
            for that meter.
          </p>
        )}
        {isLoading && (
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-[120px]'>Device ID</TableHead>
                  <TableHead>Leak Period</TableHead>
                  <TableHead className='text-right'>Leak Days</TableHead>
                  <TableHead className='text-right'>Leak Mean (GPM)</TableHead>
                  <TableHead className='text-right'>Post-Leak Mean (GPM)</TableHead>
                  <TableHead className='text-right'>% Flow Reduction</TableHead>
                  <TableHead className='text-right'>Gallons Saved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className='h-4 w-20 bg-muted animate-pulse rounded' />
                    </TableCell>
                    <TableCell>
                      <div className='h-4 w-32 bg-muted animate-pulse rounded' />
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='h-4 w-8 bg-muted animate-pulse rounded ml-auto' />
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='h-4 w-12 bg-muted animate-pulse rounded ml-auto' />
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='h-4 w-12 bg-muted animate-pulse rounded ml-auto' />
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='h-4 w-10 bg-muted animate-pulse rounded ml-auto' />
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='h-4 w-16 bg-muted animate-pulse rounded ml-auto' />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {error && (
          <div className='flex items-center justify-center h-[200px]'>
            <p className='text-sm text-destructive'>Failed to load meters: {error}</p>
          </div>
        )}

        {!isLoading && !error && (
          <div className='space-y-4'>
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-[120px]'>Device ID</TableHead>
                    <TableHead>Leak Period</TableHead>
                    <TableHead className='text-right'>Leak Days</TableHead>
                    <TableHead className='text-right'>Leak Mean (GPM)</TableHead>
                    <TableHead className='text-right'>Post-Leak Mean (GPM)</TableHead>
                    <TableHead className='text-right'>% Flow Reduction</TableHead>
                    <TableHead className='text-right'>Gallons Saved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sensors.length > 0
                    ? (
                      sensors.map((sensor) => (
                        <TableRow
                          key={sensor.sensorId}
                          onClick={onSensorClick ? () => onSensorClick(sensor.sensorId) : undefined}
                          className={onSensorClick ? 'cursor-pointer hover:bg-muted/50' : undefined}
                        >
                          <TableCell className='font-medium'>{sensor.deviceId}</TableCell>
                          <TableCell>
                            {new Date(sensor.leakStart).toLocaleDateString()} -{' '}
                            {new Date(sensor.leakEnd).toLocaleDateString()}
                          </TableCell>
                          <TableCell className='text-right'>{sensor.leakPeriodDays}</TableCell>
                          <TableCell className='text-right'>{sensor.leakMean.toFixed(2)}</TableCell>
                          <TableCell className='text-right'>
                            {sensor.postLeakMean.toFixed(2)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {sensor.pctWaterSaved.toFixed(1)}%
                          </TableCell>
                          <TableCell className='text-right font-semibold'>
                            {sensor.gallonsSaved.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )
                    : (
                      <TableRow>
                        <TableCell colSpan={7} className='h-24 text-center'>
                          No meters with fixed leaks found for this period
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className='flex items-center justify-between px-2'>
              <div className='flex-1 text-sm text-muted-foreground'>
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
                {' '}
                meters
              </div>
              <div className='flex items-center space-x-6 lg:space-x-8'>
                <div className='flex items-center space-x-2'>
                  <p className='text-sm font-medium'>Rows per page</p>
                  <Select
                    value={`${limit}`}
                    onValueChange={(value: string) => onLimitChange(Number(value))}
                  >
                    <SelectTrigger className='h-8 w-[70px]'>
                      <SelectValue placeholder={limit} />
                    </SelectTrigger>
                    <SelectContent side='top'>
                      {[10, 20, 50, 100].map((pageSize) => (
                        <SelectItem key={pageSize} value={`${pageSize}`}>
                          {pageSize}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex items-center space-x-2'>
                  <span className='text-sm font-medium'>Page</span>
                  <Input
                    type='number'
                    min={1}
                    max={totalPages || 1}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyDown={handlePageInputKeyDown}
                    onBlur={handlePageJump}
                    placeholder={`${page}`}
                    className='h-8 w-[80px] text-center'
                    aria-label='Go to page'
                  />
                  <span className='text-sm font-medium'>of {totalPages || 1}</span>
                </div>
                <div className='flex items-center space-x-2'>
                  <Button
                    variant='outline'
                    className='h-8 w-8 p-0'
                    onClick={() => onPageChange(page - 1)}
                    disabled={!canPreviousPage}
                    aria-label='Go to previous page'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </Button>
                  <Button
                    variant='outline'
                    className='h-8 w-8 p-0'
                    onClick={() => onPageChange(page + 1)}
                    disabled={!canNextPage}
                    aria-label='Go to next page'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
